import { graphql, useStaticQuery } from 'gatsby'
import React, { useCallback, useState } from 'react'
import {
  BGVariants,
  HTSVariants,
  HeroTextSection,
} from '../types/heroTextSection'
import useSiteMetadata from './useSiteMetadata'

type ComplianceQueryResult = {
  pp: HeroTextSection
  tos: HeroTextSection
}
interface ComplianceNoteContextType {
  complianceLinkText: string | null
  handleComplianceModalOpen: (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => void
  handleComplianceModalClose: () => void
  complianceModalData: ComplianceQueryResult
}

const placeholderComplianceData = {
  body: {
    raw: '',
  },
  __typename: 'ContentfulHeroTextSection',
  id: '',
  contentful_id: '',
  name: '',
  subtitle: '',
  backgroundColor: BGVariants.White,
  brand: {
    id: '',
    name: '',
    blogPrefix: '',
    logo: {
      file: {
        url: '',
      },
    },
    whiteLogo: {
      file: {
        url: '',
      },
    },
    productionUrl: '',
    favicon: null,
    phoneNumber: '',
    alternateName: [''],
    parentOrganization: '',
    isTopResume: true,
    isTopCV: false,
    isTopCVuk: false,
    isZipJob: false,
    alternateHreflang: null,
    rssFeedTitle: null,
    rssFeedHref: null,
  },
  variant: HTSVariants.Centered,
  hideOn: null,
  codeClasses: null,
  paddingUpdate: 'ZeroTopPadding',
} as HeroTextSection

const ComplianceNoteContext = React.createContext<ComplianceNoteContextType>({
  complianceLinkText: null,
  handleComplianceModalOpen: () => {
    // do nothing
  },
  handleComplianceModalClose: () => {
    // do nothing
  },
  complianceModalData: {
    pp: placeholderComplianceData,
    tos: placeholderComplianceData,
  },
})

export default function ComplianceNoteProvider(props: {
  children?: React.ReactNode
}) {
  const { children } = props
  const [complianceLinkText, setComplianceLinkText] = useState<string | null>(
    null
  )
  const { i18nextLocale } = useSiteMetadata()

  const complianceModalRawData = useStaticQuery(graphql`
    query MyQuery {
      pp_en: contentfulHeroTextSection(
        contentful_id: { eq: "6TTnggKJNIEtxoQ72QKJl3" }
        node_locale: { eq: "en-US" }
      ) {
        ...HeroTextSection
      }
      tos_en: contentfulHeroTextSection(
        contentful_id: { eq: "3FKdvAOlLw0kEwo2P9Knhv" }
        node_locale: { eq: "en-US" }
      ) {
        ...HeroTextSection
      }
      pp_fr: contentfulHeroTextSection(
        contentful_id: { eq: "6TTnggKJNIEtxoQ72QKJl3" }
        node_locale: { eq: "fr" }
      ) {
        ...HeroTextSection
      }
      tos_fr: contentfulHeroTextSection(
        contentful_id: { eq: "3FKdvAOlLw0kEwo2P9Knhv" }
        node_locale: { eq: "fr" }
      ) {
        ...HeroTextSection
      }
    }
  `)

  const complianceModalData = {
    pp:
      complianceModalRawData[`pp_${i18nextLocale}`] ??
      complianceModalRawData.pp_en,
    tos:
      complianceModalRawData[`tos_${i18nextLocale}`] ??
      complianceModalRawData.tos_en,
  }

  const handleComplianceModalOpen = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      const { textContent } = e.currentTarget

      // Trim any &nbsp; white spaces
      if (textContent) {
        setComplianceLinkText(textContent.trim())
      }
    },
    []
  )

  const handleComplianceModalClose = useCallback(() => {
    setComplianceLinkText(null)
  }, [])

  return (
    <ComplianceNoteContext.Provider
      value={{
        complianceLinkText,
        handleComplianceModalOpen,
        handleComplianceModalClose,
        complianceModalData,
      }}
    >
      {children}
    </ComplianceNoteContext.Provider>
  )
}

export function useComplianceNote(): ComplianceNoteContextType {
  return React.useContext(ComplianceNoteContext)
}
